import {
  JournalEntryLinePostingType,
  JournalEntryPostingType,
  journalEntryTotalsPostingType,
  parseJournalEntryLinePostingType,
} from "@/core/enums/JournalEntryPostingType";

export interface AccountingJournalEntryLineProps {
  account: string | null;
  qboAccountId: number | null;
  qboAccountName: string | null;
  description: string | null;
  name: string | null;
  postingType: string | null;
  amount: number;
  credits: number | null;
  debits: number | null;
  sourceType: string | null;
  sourceTypes: string[] | null;
  deductionIds: number[] | null;
  remittanceItemIds: number[] | null;
}

export class AccountingJournalEntryLine {
  private readonly _account: string | null;
  private readonly _qboAccountId: number | null;
  private readonly _qboAccountName: string | null;
  private readonly _description: string | null;
  private readonly _name: string | null;
  private readonly _postingType: JournalEntryLinePostingType | null;
  private readonly _amount: number;
  private readonly _credits: number | null;
  private readonly _debits: number | null;
  private readonly _sourceType: string | null;
  private readonly _sourceTypes: string[] | null;
  private readonly _deductionIds: number[] | null;
  private readonly _remittanceItemIds: number[] | null;

  constructor(props: AccountingJournalEntryLineProps) {
    this._account = props.account ?? null;
    this._qboAccountId = props.qboAccountId ?? null;
    this._qboAccountName = props.qboAccountName ?? null;
    this._description = props.description ?? null;
    this._name = props.name ?? null;
    this._postingType = AccountingJournalEntryLine.normalizePostingType(props.postingType);
    this._amount = Number(props.amount ?? 0);
    this._credits = props.credits === null ? null : Number(props.credits);
    this._debits = props.debits === null ? null : Number(props.debits);
    this._sourceType = props.sourceType ?? null;
    this._sourceTypes = props.sourceTypes ?? null;
    this._deductionIds = props.deductionIds ?? null;
    this._remittanceItemIds = props.remittanceItemIds ?? null;
  }

  private static normalizePostingType(
    value: string | null | undefined
  ): JournalEntryLinePostingType | null {
    if (value === journalEntryTotalsPostingType) {
      return journalEntryTotalsPostingType;
    }

    return parseJournalEntryLinePostingType(value);
  }

  public get account(): string | null {
    return this._account;
  }

  public get qboAccountId(): number | null {
    return this._qboAccountId;
  }

  public get qboAccountName(): string | null {
    return this._qboAccountName;
  }

  public get displayAccount(): string {
    return this._qboAccountName ?? this._account ?? "—";
  }

  public get description(): string | null {
    return this._description;
  }

  public get name(): string | null {
    return this._name;
  }

  public get postingType(): JournalEntryLinePostingType | null {
    return this._postingType;
  }

  public get amount(): number {
    return this._amount;
  }

  public get credits(): number | null {
    return this._credits;
  }

  public get debits(): number | null {
    return this._debits;
  }

  public get sourceType(): string | null {
    return this._sourceType;
  }

  public get sourceTypes(): string[] | null {
    return this._sourceTypes;
  }

  public get deductionIds(): number[] | null {
    return this._deductionIds;
  }

  public get remittanceItemIds(): number[] | null {
    return this._remittanceItemIds;
  }

  public get isDebitRow(): boolean {
    return this._postingType === JournalEntryPostingType.debit;
  }

  public get isTotalsRow(): boolean {
    return this._postingType === journalEntryTotalsPostingType;
  }

  public get key(): string {
    if (this.isTotalsRow) {
      return "totals";
    }
    return `${this._postingType ?? "line"}-${this._qboAccountId ?? this._account ?? "unknown"}`;
  }

  public static fromJson(props: AccountingJournalEntryLineProps): AccountingJournalEntryLine {
    return new AccountingJournalEntryLine(props);
  }
}
